// Phase 2 - Day-1 Activation Path (wire-up complete)
// audit_response §3 / §16: when doneCount < 3, the home tab IS the
// quickstart — no dashboard behind it. We achieve this without touching
// CommissionTracker.jsx by rendering a fixed full-viewport overlay that
// covers the page when the activation cliff hasn't been cleared yet.
//
// Props:
//   onDoneCountChange(n) - notifies parent of current done count
//   fullScreen           - explicit override (parent can force; default auto)
//
// Auto-fullscreen behaviour:
//   doneCount < 3 AND not snoozed → fixed overlay (position:fixed z-50)
//   doneCount >= 3 OR snoozed     → inline panel (original behaviour)

import React from 'react';
import { Upload, Megaphone, BookOpen, Mail, Users, Briefcase, CheckCircle2, X, ChevronRight, Clock, Rocket } from 'lucide-react';
import { supabase } from '../lib/supabase';

const LS_KEY = 'crm:quickstart_dismissed_v1';
const LS_SNOOZE_KEY = 'crm:quickstart_snooze_until';

// RETIRED into the Setup Tour (2026-07). The tour's "See all steps"
// expander, driven by /api/onboarding/status, is now the single
// onboarding checklist — QuickstartPanel must never render alongside it
// ("never render both"). Flip to false to restore the legacy panel.
const TOUR_OWNS_ONBOARDING = true;

export default function QuickstartPanel({ currentUser, onGotoTab, sub, posture, onDoneCountChange, fullScreen }) {
      const [dismissed, setDismissed] = React.useState(() => {
              try {
                        if (localStorage.getItem(LS_KEY) === '1') return true;
                        const snoozeUntil = Number(localStorage.getItem(LS_SNOOZE_KEY) || 0);
                        if (snoozeUntil && snoozeUntil > Date.now()) return true;
              } catch {}
              return false;
      });

  const [taskState, setTaskState] = React.useState({});

  React.useEffect(() => {
          if (!currentUser?.id) return;
          let cancelled = false;
          (async () => {
                    const ownerId = currentUser.id;
                    const [bookRes, metaRes, agentsRes, dripsRes, membersRes, rulesRes] = await Promise.all([
                                supabase.from('clients').select('id', { count: 'exact', head: true }).eq('owner_id', ownerId),
                                supabase.from('subscriptions').select('meta_page_id, meta_connected_at').eq('owner_id', ownerId).maybeSingle(),
                                supabase.from('ai_agents').select('knowledge_base, example_messages').eq('owner_id', ownerId),
                                supabase.from('email_drip_sequences').select('id', { count: 'exact', head: true }).eq('owner_id', ownerId),
                                supabase.from('organization_members').select('user_id', { count: 'exact', head: true }).eq('user_id', ownerId).neq('user_id', null),
                                supabase.from('commission_rules').select('id', { count: 'exact', head: true }).eq('owner_id', ownerId),
                              ]);
                    if (cancelled) return;
                    const agents = agentsRes?.data || [];
                    const anyTrained = agents.some(a => (a.knowledge_base || '').trim().length > 0 || (Array.isArray(a.example_messages) && a.example_messages.length > 0));
                    setTaskState({
                                book_imported: (bookRes?.count || 0) > 0,
                                meta_connected: !!(metaRes?.data?.meta_page_id || metaRes?.data?.meta_connected_at),
                                agents_trained: anyTrained,
                                drips_set: (dripsRes?.count || 0) > 0,
                                team_invited: (membersRes?.count || 0) > 1,
                                rules_set: (rulesRes?.count || 0) > 0,
                    });
          })();
          return () => { cancelled = true; };
  }, [currentUser?.id]);

  const tasks = [
      { key: 'book_imported', icon: Upload, title: 'Upload your book of business', blurb: 'CSV or paste - AI finds renewals + missed money in <30 sec.', tab: 'home' },
      { key: 'agents_trained', icon: BookOpen, title: 'Train your AI agents', blurb: 'Teach knowledge + examples so the AI sounds like you.', tab: 'settings' },
      { key: 'meta_connected', icon: Megaphone, title: 'Connect Meta Ads', blurb: 'Auto-text Facebook/Instagram leads the second they convert.', tab: 'meta_ads' },
      { key: 'drips_set', icon: Mail, title: 'Set up your first drip', blurb: "Welcome / renewal / birthday - pick a template, you're done.", tab: 'email_drips' },
      { key: 'rules_set', icon: Briefcase, title: 'Configure carrier rules', blurb: 'So chargebacks + commissions parse cleanly.', tab: 'settings' },
      { key: 'team_invited', icon: Users, title: 'Invite your team', blurb: 'VAs + producers each get scoped seats.', tab: 'settings' },
        ];
      const doneCount = tasks.filter(t => taskState[t.key]).length;
      const totalCount = tasks.length;
      const allDone = doneCount === totalCount;

  // Notify parent whenever done count changes
  const prevDoneRef = React.useRef(null);
      React.useEffect(() => {
              if (onDoneCountChange && prevDoneRef.current !== doneCount) {
                        prevDoneRef.current = doneCount;
                        onDoneCountChange(doneCount);
              }
      }, [doneCount, onDoneCountChange]);

  // Retired: the Setup Tour owns onboarding now (see flag above). Placed
  // after all hooks so React's rules-of-hooks hold.
  if (TOUR_OWNS_ONBOARDING) return null;
  if (allDone) return null;
      if (dismissed) return null;

  const handleHide = () => {
          try {
                    if (doneCount >= 3) {
                                localStorage.setItem(LS_KEY, '1');
                    } else {
                                const tomorrow = Date.now() + 24 * 60 * 60 * 1000;
                                localStorage.setItem(LS_SNOOZE_KEY, String(tomorrow));
                    }
          } catch {}
          setDismissed(true);
  };

  const a2pPending = posture && posture.brand?.status !== 'approved';

  // Determine whether to use fullscreen mode:
  // - explicit prop wins if provided (true/false)
  // - otherwise auto: fullscreen when doneCount < 3 (activation cliff not cleared)
  const useFullScreen = fullScreen !== undefined ? fullScreen : doneCount < 3;

  // Full-screen activation takeover
  if (useFullScreen) {
          return (
                    <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
                            <div className="min-h-full flex flex-col items-center justify-start pt-10 px-4 pb-16">
                                      <div className="w-full max-w-2xl">
                                                  <div className="text-center mb-8">
                                                                <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">
                                                                                <Rocket size={12}/> Activate your platform
                                                                </div>
                                                                <h1 className="text-3xl font-black text-slate-900 leading-tight">
                                                                    {a2pPending ? 'While we register your texting number...' : 'Get your AI platform running'}
                                                                </h1>
                                                                <p className="text-slate-500 mt-3 text-sm">
                                                                    {a2pPending
                                                                                          ? 'A2P carrier review takes 1-3 business days. Knock out these 6 steps while you wait.'
                                                                                          : 'Complete these steps to unlock your full AI-powered insurance platform.'}
                                                                </p>
                                                                <div className="mt-5 flex items-center gap-3 justify-center">
                                                                                <div className="flex-1 max-w-xs h-2 bg-slate-200 rounded-full overflow-hidden">
                                                                                                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                                                                                                          style={{ width: (doneCount / totalCount * 100) + '%' }}/>
                                                                                </div>
                                                                                <span className="text-sm font-bold text-slate-700">{doneCount}/{totalCount}</span>
                                                                </div>
                                                  </div>
                                                  <div className="grid grid-cols-1 gap-3">
                                                      {tasks.map((t) => {
                                        const done = !!taskState[t.key];
                                        const Icon = t.icon;
                                        return (
                                                              <button key={t.key} onClick={() => !done && onGotoTab?.(t.tab)} disabled={done}
                                                                                      className={'text-left flex items-start gap-4 p-4 rounded-xl border-2 transition-all ' + (done ? 'bg-emerald-50 border-emerald-200 cursor-default' : 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-md')}
                                                                                    >
                                                                                  <div className={'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ' + (done ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500')}>
                                                                                      {done ? <CheckCircle2 size={18}/> : <Icon size={18}/>}
                                                                                      </div>
                                                                                  <div className="min-w-0 flex-1">
                                                                                                        <div className={'font-bold text-base ' + (done ? 'text-emerald-900 line-through' : 'text-slate-900')}>{t.title}</div>
                                                                                                        <div className="text-sm text-slate-500 mt-0.5">{t.blurb}</div>
                                                                                      </div>
                                                                  {!done && (
                                                                                                              <div className="flex-shrink-0 flex items-center gap-1 text-xs font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-lg px-2 py-1">
                                                                                                                                      Start <ChevronRight size={12}/>
                                                                                                                  </div>
                                                                                  )}
                                                              </button>
                                                            );
                    })}
                                                  </div>
                                          {doneCount >= 1 && (
                                      <div className="mt-6 text-center">
                                                      <button onClick={handleHide} className="text-xs text-slate-400 hover:text-slate-700 underline">
                                                                        Skip for now - show me the dashboard
                                                      </button>
                                      </div>
                                                  )}
                                      </div>
                            </div>
                    </div>
                  );
  }
    
      // Inline panel (shown when doneCount >= 3 or operator snoozed)
      return (
              <div className="bg-gradient-to-br from-slate-50 to-emerald-50 border-2 border-emerald-200 rounded-2xl p-5 mb-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                            <div>
                                      <div className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-700">Quickstart · {doneCount}/{totalCount} done</div>
                                      <h3 className="font-bold text-slate-900 text-lg mt-1">{a2pPending ? 'While we register your texting number...' : 'Get the most out of your AI'}</h3>
                                {a2pPending && (
                              <div className="text-xs text-slate-600 mt-1 inline-flex items-center gap-1">
                                            <Clock size={11}/> A2P carrier review usually takes 1-3 business days. Knock these out while you wait.
                              </div>
                                      )}
                            </div>
                            <button onClick={handleHide} className="text-slate-400 hover:text-slate-700"
                                          title={doneCount >= 3 ? 'Hide permanently' : 'Snooze until tomorrow'}><X size={18}/></button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {tasks.map(t => {
                            const done = !!taskState[t.key];
                            const Icon = t.icon;
                            return (
                                            <button key={t.key} onClick={() => !done && onGotoTab?.(t.tab)} disabled={done}
                                                              className={'text-left flex items-start gap-3 p-3 rounded-lg border transition-colors ' + (done ? 'bg-emerald-50 border-emerald-200 cursor-default' : 'bg-white border-slate-200 hover:border-emerald-400 hover:bg-emerald-50')}
                                                            >
                                                          <div className={'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ' + (done ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600')}>
                                                              {done ? <CheckCircle2 size={16}/> : <Icon size={16}/>}
                                                          </div>
                                                          <div className="min-w-0 flex-1">
                                                                          <div className={'font-bold text-sm ' + (done ? 'text-emerald-900 line-through' : 'text-slate-900')}>{t.title}</div>
                                                                          <div className="text-xs text-slate-500 mt-0.5">{t.blurb}</div>
                                                          </div>
                                                {!done && <ChevronRight size={14} className="text-slate-400 flex-shrink-0 mt-1"/>}
                                            </button>
                                          );
              })}
                    </div>
              </div>
            );
}
